// DOBInput component with MM/DD/YYYY formatting for date of birth
// Displays as MM/DD/YYYY, outputs as YYYY-MM-DD for API

import { useState, useCallback } from 'react'

interface DOBInputProps {
  value: string // Expected in YYYY-MM-DD format
  onChange: (value: string) => void // Returns YYYY-MM-DD format
  id?: string
  name?: string
  placeholder?: string
  required?: boolean
  className?: string
}

// Format display as MM/DD/YYYY
function formatDOB(digits: string): string {
  if (digits.length === 0) return ''
  if (digits.length <= 2) return digits
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`
}

// Extract raw digits from any input
function extractDigits(value: string): string {
  return value.replace(/\D/g, '').slice(0, 8) // MMDDYYYY = 8 digits
}

// Convert MMDDYYYY digits to YYYY-MM-DD for API
function toAPIFormat(digits: string): string {
  if (digits.length < 8) return '' // Incomplete date
  const month = digits.slice(0, 2)
  const day = digits.slice(2, 4)
  const year = digits.slice(4, 8)
  return `${year}-${month}-${day}`
}

// Convert YYYY-MM-DD to MMDDYYYY digits for display
function fromAPIFormat(apiValue: string): string {
  if (!apiValue || apiValue.length < 10) return ''
  const parts = apiValue.split('-')
  if (parts.length !== 3) return ''
  const [year, month, day] = parts
  return `${month}${day}${year}`
}

export function DOBInput({
  value,
  onChange,
  id = 'dob',
  name = 'dob',
  placeholder = 'MM/DD/YYYY',
  required = false,
  className = '',
}: DOBInputProps) {
  // Convert API format (YYYY-MM-DD) to display digits (MMDDYYYY)
  const [displayValue, setDisplayValue] = useState(() => {
    const digits = fromAPIFormat(value)
    return formatDOB(digits)
  })

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value
    let digits = extractDigits(input)

    // Clamp month/day digits during typing only — skip when all 8 digits
    // arrive at once (paste, autofill) to avoid digit-eating from auto-prefix
    if (digits.length < 8) {
      // Clamp month digits to prevent obviously invalid months
      if (digits.length >= 1) {
        const m1 = parseInt(digits[0], 10)
        if (m1 > 1) {
          digits = '0' + digits // auto-prefix 0 (e.g., "3" → "03")
        }
      }
      if (digits.length >= 2) {
        const month = parseInt(digits.slice(0, 2), 10)
        if (month > 12) {
          digits = '12' + digits.slice(2)
        }
      }

      // Clamp day digits to prevent obviously invalid days
      if (digits.length >= 3) {
        const d1 = parseInt(digits[2], 10)
        if (d1 > 3) {
          digits = digits.slice(0, 2) + '0' + digits.slice(2) // auto-prefix 0
        }
      }
      if (digits.length >= 4) {
        const day = parseInt(digits.slice(2, 4), 10)
        if (day > 31) {
          digits = digits.slice(0, 2) + '31' + digits.slice(4)
        } else if (day === 0) {
          digits = digits.slice(0, 2) + '01' + digits.slice(4)
        }
      }

      // Re-apply max length after possible digit insertion
      digits = digits.slice(0, 8)
    }

    const formatted = formatDOB(digits)
    setDisplayValue(formatted)

    // Only send to parent when we have a complete date
    if (digits.length === 8) {
      onChange(toAPIFormat(digits))
    } else {
      onChange('') // Clear if incomplete
    }
  }, [onChange])

  return (
    <input
      type="text"
      id={id}
      name={name}
      value={displayValue}
      onChange={handleChange}
      placeholder={placeholder}
      required={required}
      className={className}
      autoComplete="bday"
      maxLength={10} // MM/DD/YYYY
      inputMode="numeric"
    />
  )
}
